import { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { hasRecordedIds, resolveCartLines, SHOPIFY_PRODUCTS } from "../lib/shopify";
import { PREORDER_ANGLE_THICKNESSES } from "../data/supportAngle";
import { cartWeightKg, needsFreightQuote } from "../lib/shipping";
import {
  cartCreate,
  cartLinesAdd,
  cartLinesUpdate,
  cartLinesRemove,
  cartQuery,
} from "../lib/storefrontApi";

const CART_STORAGE_KEY = "stkCart";
const CART_TOUCHED_KEY = "stkCartTouched";
// A cart untouched for this long is stale — a shopper coming back after a week
// shouldn't find last month's 20 brackets waiting. Shopify keeps carts ~10
// days anyway; this just draws the line earlier and locally.
const CART_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStoredCartId() {
  try {
    const id = localStorage.getItem(CART_STORAGE_KEY) || null;
    if (!id) return null;
    const touched = Number(localStorage.getItem(CART_TOUCHED_KEY));
    // No timestamp (pre-expiry carts) counts as fresh once — it gets stamped on
    // the next cart write. Stale carts are dropped here.
    if (touched && Date.now() - touched > CART_MAX_AGE_MS) {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(CART_TOUCHED_KEY);
      return null;
    }
    return id;
  } catch {
    return null;
  }
}

function setStoredCartId(id) {
  try {
    if (id) {
      localStorage.setItem(CART_STORAGE_KEY, id);
      localStorage.setItem(CART_TOUCHED_KEY, String(Date.now()));
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(CART_TOUCHED_KEY);
    }
  } catch { /* ignore */ }
}

// Flatten cart lines from Shopify edges format.
export function flattenLines(cart) {
  if (!cart?.lines?.edges) return [];
  return cart.lines.edges.map((e) => e.node);
}

// Total item count across all lines (sum of quantities).
export function cartItemCount(cart) {
  return flattenLines(cart).reduce((sum, line) => sum + line.quantity, 0);
}

// Shopify returns product GIDs like "gid://shopify/Product/8984400003126".
// Extract the numeric ID so we can compare against SHOPIFY_PRODUCTS constants.
function numericProductId(gid) {
  return gid ? gid.split("/").pop() : null;
}

// True when the cart contains actual star picket lines AND parcel-product lines.
// Uses product IDs rather than title keywords so bracket products with "picket"
// in their name don't incorrectly trigger the warning.
const LONG_PRODUCT_IDS = new Set([
  SHOPIFY_PRODUCTS.pickets,
  SHOPIFY_PRODUCTS.animalExtension,
]);

export function hasMixedProducts(cart) {
  const lines = flattenLines(cart);
  const ids = lines.map((l) => numericProductId(l.merchandise?.product?.id));
  const hasLongProducts = ids.some((id) => LONG_PRODUCT_IDS.has(id));
  const hasParcelProducts = ids.some((id) => id && !LONG_PRODUCT_IDS.has(id));
  return hasLongProducts && hasParcelProducts;
}

// Bracket products that carry a pre-order support-angle spec. A line counts as
// pre-order only when it's one of these products AND its "Support angle" line
// attribute is a pre-order thickness (2.5 mm Standard / 3.0 mm Heavy Duty on the
// supported bracket, or 3.0 mm on the flat bracket).
const PREORDER_PRODUCT_IDS = new Set([
  SHOPIFY_PRODUCTS.brackets,
  SHOPIFY_PRODUCTS.flatBracket,
]);

export function hasPreorderItems(cart) {
  return flattenLines(cart).some((line) => {
    const id = numericProductId(line.merchandise?.product?.id);
    if (!PREORDER_PRODUCT_IDS.has(id)) return false;
    const angle = line.attributes?.find((a) => a.key === "Support angle")?.value;
    return Boolean(angle) && PREORDER_ANGLE_THICKNESSES.has(angle.trim());
  });
}

// Total shipping weight of the cart in kg, taken from the variant weights
// Shopify returns on each line, so it matches what checkout rates the order on.
export function cartTotalWeightKg(cart) {
  return cartWeightKg(flattenLines(cart));
}

const EXTENSION_PRODUCT_IDS = new Set([
  SHOPIFY_PRODUCTS.galvanisedExtension,
  SHOPIFY_PRODUCTS.aluminiumExtension,
]);

// True when the cart is past the heaviest order Shopify still prices, so
// delivery has to be quoted as palletised freight instead. The extensions and
// the brackets/kits are on separate delivery profiles, each with its own
// ceiling, so they're weighed apart.
export function cartNeedsFreightQuote(cart) {
  const lines = flattenLines(cart);
  const isExtension = (line) =>
    EXTENSION_PRODUCT_IDS.has(numericProductId(line.merchandise?.product?.id));
  return needsFreightQuote(
    cartWeightKg(lines.filter((line) => !isExtension(line))),
    cartWeightKg(lines.filter(isExtension))
  );
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState = {
  cart: null,
  cartLoading: false,
  isCartOpen: false,
};

function cartReducer(state, action) {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, cartLoading: action.payload };
    case "SET_CART":
      return { ...state, cart: action.payload, cartLoading: false };
    case "CLEAR_CART":
      return { ...state, cart: null, cartLoading: false };
    case "OPEN_CART":
      return { ...state, isCartOpen: true };
    case "CLOSE_CART":
      return { ...state, isCartOpen: false };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // On mount: restore cart from localStorage if the ID is still valid.
  useEffect(() => {
    const storedId = getStoredCartId();
    if (!storedId) return;
    dispatch({ type: "SET_LOADING", payload: true });
    cartQuery(storedId)
      .then((cart) => {
        if (cart) {
          // Refresh the touched stamp — the 7-day window is since last visit,
          // not since the cart was first created.
          setStoredCartId(storedId);
          dispatch({ type: "SET_CART", payload: cart });
        } else {
          // Cart no longer exists on Shopify (expired / completed).
          setStoredCartId(null);
          dispatch({ type: "CLEAR_CART" });
        }
      })
      .catch(() => {
        setStoredCartId(null);
        dispatch({ type: "CLEAR_CART" });
      });
  }, []);

  // -------------------------------------------------------------------------
  // addLines — the primary entry point for product pages.
  // items: [{ productId, quantity, variantTitle?, attributes? }]
  // Resolves variant GIDs, then creates or updates the Shopify cart.
  // -------------------------------------------------------------------------
  const addLines = useCallback(async (items) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      // Resolve all variant GIDs first (may throw if product is DRAFT/not found).
      // Lines carrying a recorded variantId resolve with no network call.
      let resolvedLines = await resolveCartLines(items);

      const currentCartId = state.cart?.id || getStoredCartId();

      const addOrCreate = async (lines) => {
        if (currentCartId) {
          try {
            return await cartLinesAdd(currentCartId, lines);
          } catch {
            // Cart may have expired — create a fresh one.
            setStoredCartId(null);
            return cartCreate(lines);
          }
        }
        return cartCreate(lines);
      };

      let updatedCart;
      try {
        updatedCart = await addOrCreate(resolvedLines);
      } catch (err) {
        // A recorded variant id that has gone stale in Shopify would fail here.
        // Retry once resolving every line by title before giving up.
        if (!hasRecordedIds(items)) throw err;
        resolvedLines = await resolveCartLines(items, false);
        updatedCart = await addOrCreate(resolvedLines);
      }

      setStoredCartId(updatedCart.id);
      dispatch({ type: "SET_CART", payload: updatedCart });
    } catch (err) {
      dispatch({ type: "SET_LOADING", payload: false });
      throw err; // Let the caller surface the error.
    }
  }, [state.cart]);

  // -------------------------------------------------------------------------
  // updateLine — change the quantity of an existing cart line.
  // quantity = 0 is equivalent to removeLine.
  // -------------------------------------------------------------------------
  const updateLine = useCallback(async (lineId, quantity) => {
    if (!state.cart?.id) return;
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const updatedCart = await cartLinesUpdate(state.cart.id, [{ id: lineId, quantity }]);
      dispatch({ type: "SET_CART", payload: updatedCart });
    } catch {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.cart]);

  // -------------------------------------------------------------------------
  // removeLine — remove a line from the cart entirely.
  // -------------------------------------------------------------------------
  const removeLine = useCallback(async (lineId) => {
    if (!state.cart?.id) return;
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const updatedCart = await cartLinesRemove(state.cart.id, [lineId]);
      dispatch({ type: "SET_CART", payload: updatedCart });
    } catch {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [state.cart]);

  // -------------------------------------------------------------------------
  // clearCart — discard the local cart reference (does not delete on Shopify).
  // -------------------------------------------------------------------------
  const clearCart = useCallback(() => {
    setStoredCartId(null);
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const openCart = useCallback(() => dispatch({ type: "OPEN_CART" }), []);
  const closeCart = useCallback(() => dispatch({ type: "CLOSE_CART" }), []);

  const value = {
    cart: state.cart,
    cartLoading: state.cartLoading,
    isCartOpen: state.isCartOpen,
    addLines,
    updateLine,
    removeLine,
    clearCart,
    openCart,
    closeCart,
    checkoutUrl: state.cart?.checkoutUrl ?? null,
    itemCount: cartItemCount(state.cart),
    lines: flattenLines(state.cart),
    subtotal: state.cart?.cost?.subtotalAmount ?? null,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
